import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';

interface Campaign {
  id: string;
  nome: string;
  status: string;
  totalContacts: number;
  sentCount: number;
  failedCount: number;
  startedAt: string | null;
  completedAt: string | null;
  criadoEm: string;
}

export function CampaignReportsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const perPage = 10;

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = { 'Content-Type': 'application/json', ...options.headers };
    if (token) (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authenticatedFetch('/api/campaigns?limit=1000')
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('Falha ao carregar')))
      .then((data) => {
        if (!cancelled) setCampaigns(data.campaigns ?? []);
      })
      .catch(() => { if (!cancelled) setCampaigns([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const sentCampaigns = useMemo(() =>
    campaigns.filter((c) => c.status === 'RUNNING' || c.status === 'COMPLETED'),
    [campaigns]
  );

  const monthCampaigns = useMemo(() => {
    const month = selectedMonth.getMonth();
    const year = selectedMonth.getFullYear();
    return sentCampaigns.filter((c) => {
      const d = c.startedAt ? new Date(c.startedAt) : new Date(c.criadoEm);
      return d.getMonth() === month && d.getFullYear() === year;
    });
  }, [sentCampaigns, selectedMonth]);

  const stats = useMemo(() => {
    const total = monthCampaigns.length;
    const totalSent = monthCampaigns.reduce((s, c) => s + (c.sentCount || 0), 0);
    const totalFailed = monthCampaigns.reduce((s, c) => s + (c.failedCount || 0), 0);
    const totalContacts = monthCampaigns.reduce((s, c) => s + (c.totalContacts || 0), 0);
    const successRate = totalContacts > 0 ? Math.round((totalSent / totalContacts) * 100) : 0;
    return { total, totalSent, totalFailed, totalContacts, successRate };
  }, [monthCampaigns]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return monthCampaigns;
    return monthCampaigns.filter((c) => c.nome.toLowerCase().includes(q));
  }, [monthCampaigns, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, currentPage]);

  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–';

  const getStatusBadge = (status: string) => {
    const map: Record<string, { label: string; className: string }> = {
      RUNNING: { label: 'Em execução', className: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' },
      COMPLETED: { label: 'Concluída', className: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' },
    };
    const t = map[status] || { label: status, className: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium ${t.className}`}>{t.label}</span>;
  };

  const monthLabel = useMemo(
    () => selectedMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
    [selectedMonth]
  );

  const goToPrevMonth = () => {
    setCurrentPage(1);
    setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentPage(1);
    setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  return (
    <>
      <Header title="Relatórios" compactTopbar />
      <PageLayout>
        <PageHeader
          title="Relatórios de Campanhas"
          breadcrumbs={[
            { label: 'Campanhas', href: '/campanhas' },
            { label: 'Relatórios' },
          ]}
        />

        <div className="space-y-6">
          {/* Cards resumo */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Visão geral</h2>
              <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/40 px-2 py-1.5 text-sm text-gray-700 dark:text-gray-200">
                <button
                  type="button"
                  onClick={goToPrevMonth}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                  aria-label="Mês anterior"
                >
                  <i className="fas fa-chevron-left text-xs" aria-hidden />
                </button>
                <span className="px-2 font-medium capitalize">{monthLabel}</span>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                  aria-label="Próximo mês"
                >
                  <i className="fas fa-chevron-right text-xs" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    setCurrentPage(1);
                    setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1));
                  }}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                >
                  <i className="fas fa-calendar-day text-xs" aria-hidden />
                  Hoje
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                    <i className="fas fa-bullhorn" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Campanhas enviadas</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {loading ? '–' : stats.total}
                </h3>
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 text-xl" aria-hidden>
                    <i className="fas fa-paper-plane" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Mensagens enviadas</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {loading ? '–' : stats.totalSent.toLocaleString('pt-BR')}
                </h3>
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                    <i className="fas fa-chart-line" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Taxa de sucesso</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {loading ? '–' : `${stats.successRate}%`}
                </h3>
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-red-600 dark:text-red-400 text-xl" aria-hidden>
                    <i className="fas fa-triangle-exclamation" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Mensagens com erro</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {loading ? '–' : stats.totalFailed.toLocaleString('pt-BR')}
                </h3>
              </div>
            </div>
          </div>

          {/* Lista de campanhas */}
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Campanhas enviadas</h2>
              <div className="flex items-center gap-3">
                <div className="relative w-56">
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm" aria-hidden />
                  <input
                    type="text"
                    placeholder="Buscar por nome..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setSearch(''); setCurrentPage(1); }}
                  className="btn-atualizar"
                >
                  <i className="fas fa-arrows-rotate text-base shrink-0" aria-hidden />
                  Atualizar
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-600 border-t-emerald-500" />
                <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Carregando...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <div className="text-5xl text-gray-300 dark:text-gray-600 mb-3" aria-hidden><i className="fas fa-chart-line" /></div>
                <p className="text-gray-600 dark:text-gray-400 font-medium">Nenhuma campanha enviada ainda</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Os relatórios aparecem aqui quando você enviar campanhas.</p>
                <button
                  type="button"
                  onClick={() => navigate('/campanhas')}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                  Ir para Campanhas
                </button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Campanha</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Início</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contatos</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Enviadas</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Falhas</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ação</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                      {paginated.map((c) => (
                        <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">{c.nome}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(c.status)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{formatDate(c.startedAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{c.totalContacts}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-emerald-600 dark:text-emerald-400">{c.sentCount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">{c.failedCount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              type="button"
                              onClick={() => navigate(`/campanhas/relatorios/${c.id}`)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 font-medium transition-colors text-sm"
                            >
                              <i className="fas fa-chart-line text-sm shrink-0" aria-hidden />
                              Ver relatório
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Mostrando {((currentPage - 1) * perPage) + 1}–{Math.min(currentPage * perPage, filtered.length)} de {filtered.length}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:pointer-events-none"
                      >
                        Anterior
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Página {currentPage} de {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:pointer-events-none"
                      >
                        Próximo
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </PageLayout>
    </>
  );
}
